import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton,
  Chip,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { Check, Close, RadioButtonUnchecked } from "@mui/icons-material";
import { format, isSameDay, parseISO } from "date-fns";

const STICKY_TAG = 130;
const STICKY_ALIAS = 130;

function DayStatusIcon({ isPresent, isAbsent }) {
  if (isPresent) return <Check fontSize="small" />;
  if (isAbsent) return <Close fontSize="small" />;
  return <RadioButtonUnchecked fontSize="small" />;
}

export default function AttendanceTable({
  animals,
  daysInMonth,
  selectedDate,
  onStatusClick,
  onAnimalClick,
}) {
  const normalizeDate = (date) => {
    if (!date) return new Date();
    if (date instanceof Date) return new Date(date);
    if (typeof date === "string") return parseISO(date);
    return new Date();
  };

  const getStatusForDay = (animal, day) => {
    const targetDate = normalizeDate(day);
    const record = animal.records.find((r) => {
      const recordDate = normalizeDate(r.date);
      return isSameDay(recordDate, targetDate);
    });
    return record ? record.status : null;
  };

  const normalizedDays = daysInMonth.map(normalizeDate);

  const stickyCellSx = (left) => ({
    position: "sticky",
    left,
    zIndex: 3,
    bgcolor: "background.paper",
    backgroundImage:
      "linear-gradient(to right, rgba(0,0,0,0.06), transparent)",
    backgroundSize: "6px 100%",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right top",
    minWidth: left === 0 ? STICKY_TAG : STICKY_ALIAS,
    boxShadow: "4px 0 8px -4px rgba(0,0,0,0.12)",
  });

  const stickyHeaderSx = (left) => ({
    ...stickyCellSx(left),
    zIndex: 5,
    fontWeight: 600,
  });

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: { md: "calc(100vh - 280px)" },
        overflow: "auto",
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={stickyHeaderSx(0)}>Tag number</TableCell>
            <TableCell sx={stickyHeaderSx(STICKY_TAG)}>Alias</TableCell>
            {normalizedDays.map((day) => {
              const dateKey = day.getTime();
              return (
                <TableCell key={dateKey} align="center" sx={{ minWidth: 52 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="caption" fontWeight={600}>
                      {format(day, "d")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {format(day, "EEE")}
                    </Typography>
                  </Box>
                </TableCell>
              );
            })}
            <TableCell sx={{ minWidth: 120, fontWeight: 600 }}>Month</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {animals.map((animal) => (
            <TableRow key={animal.id} hover>
              <TableCell sx={stickyCellSx(0)}>
                <Tooltip title={`View ${animal.alias || animal.tag_number}`}>
                  <Button
                    color="primary"
                    size="small"
                    onClick={() => onAnimalClick(animal.id)}
                    sx={{ textTransform: "none", px: 0.5 }}
                  >
                    {animal.tag_number}
                  </Button>
                </Tooltip>
              </TableCell>
              <TableCell sx={stickyCellSx(STICKY_TAG)}>{animal.alias || "—"}</TableCell>

              {normalizedDays.map((day) => {
                const status = getStatusForDay(animal, day);
                const dateKey = day.getTime();
                const isPresent = status === "Present";
                const isAbsent = status === "Absent";

                return (
                  <TableCell key={dateKey} align="center" sx={{ p: 0.5 }}>
                    <Tooltip
                      title={`${isPresent ? "Present" : isAbsent ? "Absent" : "Unchecked"} — tap to edit`}
                    >
                      <IconButton
                        aria-label="Edit attendance"
                        size="medium"
                        onClick={() =>
                          onStatusClick(animal.id, day, status)
                        }
                        sx={{
                          minWidth: 44,
                          minHeight: 44,
                          bgcolor: isPresent
                            ? "success.light"
                            : isAbsent
                              ? "error.light"
                              : "grey.200",
                          "&:hover": {
                            bgcolor: isPresent
                              ? "success.main"
                              : isAbsent
                                ? "error.main"
                                : "grey.400",
                          },
                          transition: "background-color 0.2s ease",
                        }}
                      >
                        <DayStatusIcon isPresent={isPresent} isAbsent={isAbsent} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                );
              })}

              <TableCell>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Tooltip title="Present days / days in month">
                    <Chip
                      label={`${
                        animal.records.filter((r) => {
                          const recordDate = normalizeDate(r.date);
                          return (
                            recordDate.getMonth() === selectedDate.getMonth() &&
                            recordDate.getFullYear() ===
                              selectedDate.getFullYear() &&
                            r.status === "Present"
                          );
                        }).length
                      }/${normalizedDays.length}`}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
